import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DateRangeIcon from "@mui/icons-material/DateRange";
import RefreshIcon from "@mui/icons-material/Refresh";
import KpiDashboardPanel from "components/KpiDashboardPanel";
import { useGetKpiDashboardQuery } from "api/homeApi";
import {
  KPI_RANGE_PRESETS,
  createPreviousRange,
  createRelativeRange,
  formatKpiRange,
  fromLocalDateTimeInput,
  isValidTimeRange,
  toLocalDateTimeInput,
} from "utils/kpiDashboard";

const DEFAULT_PRESET = KPI_RANGE_PRESETS.find((preset) => preset.key === "24h");

const getPanels = (data) => (Array.isArray(data?.panels) ? data.panels : []);

const getPanelSpan = (panel) => {
  const grafanaWidth = panel.gridPos?.w || panel.layout?.w;
  if (grafanaWidth) return Math.min(12, Math.max(3, Math.ceil(grafanaWidth / 2)));
  return ["stat", "gauge"].includes((panel.type || "").toLowerCase()) ? 3 : 6;
};

const emptyComparisonPanel = (panel) => ({
  ...panel,
  value: null,
  formattedValue: null,
  series: [],
  rows: [],
  text: "",
});

function RangeDialog({ open, title, range, onClose, onApply }) {
  const [fromValue, setFromValue] = useState("");
  const [toValue, setToValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setFromValue(toLocalDateTimeInput(range.from));
    setToValue(toLocalDateTimeInput(range.to));
    setError("");
  }, [open, range]);

  const handleApply = () => {
    const nextRange = {
      from: fromLocalDateTimeInput(fromValue),
      to: fromLocalDateTimeInput(toValue),
    };
    if (!isValidTimeRange(nextRange)) {
      setError("Start time must be earlier than end time.");
      return;
    }
    onApply(nextRange);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="From"
            type="datetime-local"
            value={fromValue}
            onChange={(event) => setFromValue(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="To"
            type="datetime-local"
            value={toValue}
            onChange={(event) => setToValue(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}

const DashboardSkeleton = () => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
    {[0, 1, 2, 3].map((item) => (
      <Skeleton key={item} variant="rounded" height={item < 2 ? 220 : 360} sx={{ borderRadius: 1 }} />
    ))}
  </Box>
);

const getErrorMessage = (error, title) => {
  const status = error?.status || error?.originalStatus;
  if (status === 404) return `${title} is not configured on the KPI dashboard service yet.`;
  if (status === 401 || status === 403) return "You do not have permission to load this dashboard.";
  return `Unable to load ${title} dashboard data.`;
};

export default function KpiDashboardBeta({ siteKey, title }) {
  const [primaryRange, setPrimaryRange] = useState(() => createRelativeRange(DEFAULT_PRESET.durationMs));
  const [primaryPreset, setPrimaryPreset] = useState(DEFAULT_PRESET.key);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [comparisonMode, setComparisonMode] = useState("previous");
  const [comparisonRange, setComparisonRange] = useState(() => (
    createPreviousRange(createRelativeRange(DEFAULT_PRESET.durationMs))
  ));
  const [primaryDialogOpen, setPrimaryDialogOpen] = useState(false);
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);

  const primaryArgs = useMemo(() => ({
    siteKey,
    from: primaryRange.from,
    to: primaryRange.to,
  }), [siteKey, primaryRange.from, primaryRange.to]);
  const comparisonArgs = useMemo(() => ({
    siteKey,
    from: comparisonRange.from,
    to: comparisonRange.to,
  }), [siteKey, comparisonRange.from, comparisonRange.to]);

  const primaryQuery = useGetKpiDashboardQuery(primaryArgs, {
    refetchOnMountOrArgChange: true,
  });
  const comparisonQuery = useGetKpiDashboardQuery(comparisonArgs, {
    skip: !compareEnabled,
    refetchOnMountOrArgChange: true,
  });

  const applyPrimaryRange = (nextRange, presetKey = null) => {
    setPrimaryRange(nextRange);
    setPrimaryPreset(presetKey);
    if (compareEnabled && comparisonMode === "previous") {
      setComparisonRange(createPreviousRange(nextRange));
    }
  };

  const handlePresetChange = (_event, presetKey) => {
    if (!presetKey) return;
    const preset = KPI_RANGE_PRESETS.find((item) => item.key === presetKey);
    if (preset) applyPrimaryRange(createRelativeRange(preset.durationMs), preset.key);
  };

  const handleCompareToggle = () => {
    setCompareEnabled((enabled) => {
      if (!enabled) {
        setComparisonMode("previous");
        setComparisonRange(createPreviousRange(primaryRange));
      }
      return !enabled;
    });
  };

  const handleComparisonApply = (nextRange) => {
    setComparisonMode("custom");
    setComparisonRange(nextRange);
  };

  const handlePreviousPeriod = () => {
    setComparisonMode("previous");
    setComparisonRange(createPreviousRange(primaryRange));
  };

  const handleRefresh = () => {
    primaryQuery.refetch();
    if (compareEnabled) comparisonQuery.refetch();
  };

  const panels = getPanels(primaryQuery.data);
  const comparisonPanels = useMemo(
    () => new Map(getPanels(comparisonQuery.data).map((panel) => [String(panel.id), panel])),
    [comparisonQuery.data]
  );
  const initialLoading = primaryQuery.isLoading || (compareEnabled && comparisonQuery.isLoading);
  const refreshing = primaryQuery.isFetching || (compareEnabled && comparisonQuery.isFetching);
  const generatedAt = primaryQuery.data?.generatedAt || primaryQuery.data?.generated_at;

  return (
    <Box sx={{ minHeight: "calc(100vh - 3.5rem)", backgroundColor: "#F4F7FE", p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1800, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography component="h1" variant="h4" sx={{ color: "#0A2240", fontWeight: 700 }}>
                {title}
              </Typography>
              <Chip label="Beta" size="small" color="primary" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              KPI Dashboard
              {generatedAt ? ` · Updated ${new Date(generatedAt).toLocaleString()}` : ""}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant={compareEnabled ? "contained" : "outlined"}
              startIcon={<CompareArrowsIcon />}
              onClick={handleCompareToggle}
            >
              Compare
            </Button>
            <Tooltip title="Refresh dashboard">
              <span>
                <IconButton onClick={handleRefresh} disabled={refreshing} aria-label="Refresh dashboard">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Paper
          square
          variant="outlined"
          sx={{ mb: 2, borderColor: "#D8E0EA", backgroundColor: "#FFFFFF" }}
        >
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={primaryPreset}
              onChange={handlePresetChange}
              aria-label="Dashboard time range"
            >
              {KPI_RANGE_PRESETS.map((preset) => (
                <ToggleButton key={preset.key} value={preset.key}>{preset.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<DateRangeIcon />}
              onClick={() => setPrimaryDialogOpen(true)}
              sx={{ minHeight: 40, textAlign: "left", whiteSpace: "normal" }}
            >
              {formatKpiRange(primaryRange)}
            </Button>
          </Box>
          {compareEnabled && (
            <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.5, borderTop: "1px solid #E7ECF2", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "#172B4D", fontWeight: 700 }}>
                Comparison
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DateRangeIcon />}
                onClick={() => setComparisonDialogOpen(true)}
                sx={{ minHeight: 40, textAlign: "left", whiteSpace: "normal" }}
              >
                {formatKpiRange(comparisonRange)}
              </Button>
              <Button
                size="small"
                variant={comparisonMode === "previous" ? "contained" : "text"}
                onClick={handlePreviousPeriod}
              >
                Previous period
              </Button>
            </Box>
          )}
          {refreshing && !initialLoading && <LinearProgress />}
        </Paper>

        {primaryQuery.error ? (
          <Alert severity="error" action={<Button color="inherit" onClick={handleRefresh}>Retry</Button>}>
            {getErrorMessage(primaryQuery.error, title)}
          </Alert>
        ) : initialLoading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {compareEnabled && comparisonQuery.error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The comparison period could not be loaded. Primary period data is still shown.
              </Alert>
            )}
            {!panels.length ? (
              <Alert severity="info">No panel data was returned for this time range.</Alert>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 2 }}>
                {panels.map((panel, index) => {
                  const panelKey = String(panel.id ?? index);
                  const comparisonPanel = compareEnabled
                    ? comparisonPanels.get(panelKey) || emptyComparisonPanel(panel)
                    : null;
                  return (
                    <Box
                      key={panelKey}
                      sx={{
                        gridColumn: {
                          xs: "span 12",
                          md: `span ${compareEnabled ? 12 : getPanelSpan(panel)}`,
                        },
                        minWidth: 0,
                      }}
                    >
                      <KpiDashboardPanel
                        panel={panel}
                        comparisonPanel={comparisonPanel}
                        primaryLabel={formatKpiRange(primaryRange)}
                        comparisonLabel={formatKpiRange(comparisonRange)}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>

      <RangeDialog
        open={primaryDialogOpen}
        title="Dashboard range"
        range={primaryRange}
        onClose={() => setPrimaryDialogOpen(false)}
        onApply={(range) => applyPrimaryRange(range)}
      />
      <RangeDialog
        open={comparisonDialogOpen}
        title="Comparison range"
        range={comparisonRange}
        onClose={() => setComparisonDialogOpen(false)}
        onApply={handleComparisonApply}
      />
    </Box>
  );
}